import { isHlsSource } from "./sourceSelection";

function noop() {}

function canPlayNativeHls(video) {
  if (!video || typeof video.canPlayType !== "function") return false;

  try {
    return Boolean(
      video.canPlayType("application/vnd.apple.mpegurl") ||
        video.canPlayType("application/x-mpegURL")
    );
  } catch (error) {
    return false;
  }
}

/**
 * Attach a VAST media file to a video element.
 *
 * MP4 and natively supported HLS use the video element directly. Other HLS
 * sources use the legacy-compatible hls.js build loaded by public/index.html.
 * The returned function must be called when the creative changes or unmounts.
 */
export function attachAdMediaSource(video, media, onFatalError) {
  const sourceUrl = media && media.url ? String(media.url) : "";
  const fail =
    typeof onFatalError === "function" ? onFatalError : noop;

  if (!video || !sourceUrl) {
    fail(new Error("Advertisement media source is missing"));
    return noop;
  }

  if (!isHlsSource(media) || canPlayNativeHls(video)) {
    video.src = sourceUrl;
    video.load();
    return noop;
  }

  const Hls =
    typeof window !== "undefined" && window.Hls ? window.Hls : null;
  if (!Hls || typeof Hls.isSupported !== "function" || !Hls.isSupported()) {
    fail(new Error("HLS playback is not supported on this device"));
    return noop;
  }

  let hls;
  let destroyed = false;
  let networkRecoveryAttempts = 0;
  let mediaRecoveryAttempts = 0;

  try {
    hls = new Hls({
      enableWorker: true,
      lowLatencyMode: false,
      backBufferLength: 30,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      fragLoadingMaxRetry: 6,
      manifestLoadingMaxRetry: 4,
      levelLoadingMaxRetry: 4,
      startLevel: -1,
      capLevelToPlayerSize: true,
    });

    hls.on(Hls.Events.ERROR, function (event, data) {
      if (destroyed || !data || !data.fatal) return;

      if (
        data.type === Hls.ErrorTypes.NETWORK_ERROR &&
        networkRecoveryAttempts === 0
      ) {
        networkRecoveryAttempts += 1;
        hls.startLoad();
        return;
      }

      if (
        data.type === Hls.ErrorTypes.MEDIA_ERROR &&
        mediaRecoveryAttempts === 0
      ) {
        mediaRecoveryAttempts += 1;
        hls.recoverMediaError();
        return;
      }

      fail(data);
    });

    hls.loadSource(sourceUrl);
    hls.attachMedia(video);
  } catch (error) {
    if (hls && typeof hls.destroy === "function") {
      hls.destroy();
    }
    fail(error);
    return noop;
  }

  return function detachAdMediaSource() {
    destroyed = true;
    if (hls && typeof hls.destroy === "function") {
      hls.destroy();
    }
  };
}
